import "./App.css";
import usePrerenderCleanup from "./lib/usePrerenderCleanup";
import { useState, useEffect, lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ClerkProvider } from "@clerk/clerk-react";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { ClerkAuthProvider, LegacyAuthProvider } from "./lib/auth";
import api from "./lib/api";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import ViewAsBanner from "./components/ViewAsBanner";
import { applyAppConfig } from "./lib/appConfig";
import { navigateToNativeAppUrl } from "./lib/nativeApp";
import { legacyRevisionTakeawaysTarget } from "./lib/adminResourceRoutes";

const Landing = lazy(() => import("./pages/Landing"));
const SubjectRevisionPage = lazy(() => import("./pages/SubjectRevisionPage"));
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NewSubmission = lazy(() => import("./pages/NewSubmission"));
const Results = lazy(() => import("./pages/Results"));
const Settings = lazy(() => import("./pages/Settings"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSchemes = lazy(() => import("./pages/admin/AdminSchemes"));
const AdminBoards = lazy(() => import("./pages/admin/AdminBoards"));
const AdminSubjects = lazy(() => import("./pages/admin/AdminSubjects"));
const AdminImportExport = lazy(() => import("./pages/admin/AdminImportExport"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminErrors = lazy(() => import("./pages/admin/AdminErrors"));
const SharedResults = lazy(() => import("./pages/SharedResults"));
const Compliance = lazy(() => import("./pages/Compliance"));
const Terms = lazy(() => import("./pages/Terms"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Analysis = lazy(() => import("./pages/Analysis"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
const ParentSubmissions = lazy(() => import("./pages/ParentSubmissions"));
const AdminParentLinks = lazy(() => import("./pages/admin/AdminParentLinks"));
const AdminDevTools = lazy(() => import("./pages/admin/AdminDevTools"));
const AdminSystemSettings = lazy(() => import("./pages/admin/AdminSystemSettings"));
const AdminPrompts = lazy(() => import("./pages/admin/AdminPrompts"));
const AdminSpecifications = lazy(() => import("./pages/admin/AdminSpecifications"));
const AdminArchive = lazy(() => import("./pages/admin/AdminArchive"));
const AdminWebhookEvents = lazy(() => import("./pages/admin/AdminWebhookEvents"));
const AdminQuestionBank = lazy(() => import("./pages/admin/QuestionBank"));
const Specification = lazy(() => import("./pages/Specification"));
const MindMaps = lazy(() => import("./pages/MindMaps"));
const Timelines = lazy(() => import("./pages/Timelines"));
const SharedMindMap = lazy(() => import("./pages/SharedMindMap"));
const SharedTimeline = lazy(() => import("./pages/SharedTimeline"));
const ExamMode = lazy(() => import("./pages/ExamMode"));
const ExamPractice = lazy(() => import("./pages/ExamPractice"));
const NewExamPractice = lazy(() => import("./pages/NewExamPractice"));
const MockExam = lazy(() => import("./pages/MockExam"));
const MockExamSession = lazy(() => import("./pages/MockExamSession"));
const MockExamResults = lazy(() => import("./pages/MockExamResults"));
const Flashcards = lazy(() => import("./pages/Flashcards"));
const FlashcardNew = lazy(() => import("./pages/FlashcardNew"));
const FlashcardImport = lazy(() => import("./pages/FlashcardImport"));
const FlashcardDetail = lazy(() => import("./pages/FlashcardDetail"));
const SharedFlashcards = lazy(() => import("./pages/SharedFlashcards"));
const SsoCallback = lazy(() => import("./pages/SsoCallback"));
const LanguagePractice = lazy(() => import("./pages/LanguagePractice"));
const VocabProgress = lazy(() => import("./pages/VocabProgress"));
const EquationPractice = lazy(() => import("./pages/EquationPractice"));
const EquationProgress = lazy(() => import("./pages/EquationProgress"));
const Timetable = lazy(() => import("./pages/Timetable"));
const TimetableNew = lazy(() => import("./pages/TimetableNew"));
const TimetableView = lazy(() => import("./pages/TimetableView"));
const TimetableDetail = lazy(() => import("./pages/TimetableDetail"));
const ExamTimetable = lazy(() => import("./pages/ExamTimetable"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Pricing = lazy(() => import("./pages/Pricing"));
const RevisionLog = lazy(() => import("./pages/RevisionLog"));
const ParentRevisionLog = lazy(() => import("./pages/ParentRevisionLog"));
const CoParentAccept = lazy(() => import("./pages/CoParentAccept"));
const Help = lazy(() => import("./pages/Help"));
const Changelog = lazy(() => import("./pages/Changelog"));
const ResourceLibrary = lazy(() => import("./pages/ResourceLibrary"));
const MyResources = lazy(() => import("./pages/MyResources"));
const ResourceReader = lazy(() => import("./pages/ResourceReader"));
const AdminResources = lazy(() => import("./pages/admin/AdminResources"));
const AdminScienceModels = lazy(() => import("./pages/admin/AdminScienceModels"));
const AdminStudentUploads = lazy(() => import("./pages/admin/AdminStudentUploads"));
const AdminQuoteBank = lazy(() => import("./pages/quote-bank-ui/AdminQuoteBank"));
const AdminStudyMaterials = lazy(() => import("./pages/study-catalogue/AdminStudyMaterials"));
const MyQuotes = lazy(() => import("./pages/quote-bank-ui/MyQuotes"));

function LegacyLibraryRedirect({ kind }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (kind) params.set("kind", kind);
  return <Navigate to={{ pathname: "/resources", search: params.toString() ? `?${params.toString()}` : "" }} replace />;
}

function AdminRoute({ children }) {
  return <ProtectedRoute adminOnly>{children}</ProtectedRoute>;
}

function MindMapShareRedirect() {
  const { token } = useParams();
  return <Navigate to={`/shared/mind-map/${token}`} replace />;
}

function LegacyRevisionTakeawaysRedirect() {
  const location = useLocation();
  return <Navigate to={legacyRevisionTakeawaysTarget(location.search)} replace />;
}

function AppRouter() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/gcse/aqa-biology" element={<SubjectRevisionPage pageSlug="aqa-biology" />} />
        <Route path="/gcse/aqa-chemistry" element={<SubjectRevisionPage pageSlug="aqa-chemistry" />} />
        <Route path="/gcse/aqa-physics" element={<SubjectRevisionPage pageSlug="aqa-physics" />} />
        <Route path="/gcse/ocr-mathematics" element={<SubjectRevisionPage pageSlug="ocr-mathematics" />} />
        <Route path="/gcse/ocr-computer-science" element={<SubjectRevisionPage pageSlug="ocr-computer-science" />} />
        <Route path="/gcse/aqa-english-literature" element={<SubjectRevisionPage pageSlug="aqa-english-literature" />} />
        <Route path="/login" element={<Login />} />
        <Route path="/sso-callback" element={<SsoCallback />} />
        <Route path="/shared/:token" element={<SharedResults />} />
        <Route path="/shared/mind-map/:token" element={<SharedMindMap />} />
        <Route path="/shared/flashcards/:token" element={<SharedFlashcards />} />
        <Route path="/shared/timeline/:token" element={<SharedTimeline />} />
        <Route path="/mindmap/shared/:token" element={<MindMapShareRedirect />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/parent" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
        <Route path="/parent-dashboard" element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
        <Route path="/new" element={<ProtectedRoute><NewSubmission /></ProtectedRoute>} />
        <Route path="/results/:id" element={<ProtectedRoute><Results /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
         <Route path="/resources" element={<ProtectedRoute><ResourceLibrary /></ProtectedRoute>} />
         <Route path="/resources/my" element={<ProtectedRoute><MyResources /></ProtectedRoute>} />
         <Route path="/resources/my/:id" element={<ProtectedRoute><MyResources /></ProtectedRoute>} />
         <Route path="/resources/:id" element={<ProtectedRoute><ResourceReader /></ProtectedRoute>} />
        <Route path="/quotes" element={<ProtectedRoute><LegacyLibraryRedirect kind="quotes" /></ProtectedRoute>} />
        <Route path="/quotes/my" element={<ProtectedRoute><MyQuotes /></ProtectedRoute>} />
        <Route path="/analysis" element={<ProtectedRoute><Analysis /></ProtectedRoute>} />
        <Route path="/specification" element={<ProtectedRoute><Specification /></ProtectedRoute>} />
        <Route path="/mind-maps" element={<ProtectedRoute><MindMaps /></ProtectedRoute>} />
         <Route path="/study-library" element={<ProtectedRoute><LegacyLibraryRedirect kind="study" /></ProtectedRoute>} />
        <Route path="/timelines" element={<ProtectedRoute><Timelines /></ProtectedRoute>} />
        <Route path="/exam/:schemeId" element={<ProtectedRoute><ExamMode /></ProtectedRoute>} />
        <Route path="/exam-practice" element={<ProtectedRoute><ExamPractice /></ProtectedRoute>} />
        <Route path="/exam-practice/new" element={<ProtectedRoute><NewExamPractice /></ProtectedRoute>} />
        <Route path="/mock-exam" element={<ProtectedRoute><MockExam /></ProtectedRoute>} />
        <Route path="/mock-exam/:id" element={<ProtectedRoute><MockExamSession /></ProtectedRoute>} />
        <Route path="/mock-exam/:id/results" element={<ProtectedRoute><MockExamResults /></ProtectedRoute>} />
        <Route path="/flashcards" element={<ProtectedRoute><Flashcards /></ProtectedRoute>} />
        <Route path="/flashcards/new" element={<ProtectedRoute><FlashcardNew /></ProtectedRoute>} />
        <Route path="/flashcards/import" element={<ProtectedRoute><FlashcardImport /></ProtectedRoute>} />
        <Route path="/flashcards/:id" element={<ProtectedRoute><FlashcardDetail /></ProtectedRoute>} />
        <Route path="/language-practice" element={<ProtectedRoute><LanguagePractice /></ProtectedRoute>} />
        <Route path="/language-practice/progress" element={<ProtectedRoute><VocabProgress /></ProtectedRoute>} />
        <Route path="/equation-practice" element={<ProtectedRoute><EquationPractice /></ProtectedRoute>} />
        <Route path="/equation-progress" element={<ProtectedRoute><EquationProgress /></ProtectedRoute>} />
        <Route path="/timetable" element={<ProtectedRoute><Timetable /></ProtectedRoute>} />
        <Route path="/timetable/new" element={<ProtectedRoute><TimetableNew /></ProtectedRoute>} />
        <Route path="/timetable/:id" element={<ProtectedRoute><TimetableView /></ProtectedRoute>} />
        <Route path="/timetable/:id/edit" element={<ProtectedRoute><TimetableDetail /></ProtectedRoute>} />
        <Route path="/exam-timetable" element={<ProtectedRoute><ExamTimetable /></ProtectedRoute>} />
        <Route path="/revision" element={<ProtectedRoute><RevisionLog /></ProtectedRoute>} />
        <Route path="/revision/child/:childId" element={<ProtectedRoute><ParentRevisionLog /></ProtectedRoute>} />
        <Route path="/parent/submissions/:childId" element={<ProtectedRoute><ParentSubmissions /></ProtectedRoute>} />
        <Route path="/co-parent/accept" element={<CoParentAccept />} />

        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/admin/schemes" element={<AdminRoute><AdminSchemes /></AdminRoute>} />
        <Route path="/admin/boards" element={<AdminRoute><AdminBoards /></AdminRoute>} />
        <Route path="/admin/subjects" element={<AdminRoute><AdminSubjects /></AdminRoute>} />
        <Route path="/admin/import-export" element={<AdminRoute><AdminImportExport /></AdminRoute>} />
        <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
        <Route path="/admin/errors" element={<AdminRoute><AdminErrors /></AdminRoute>} />
        <Route path="/admin/parent-links" element={<AdminRoute><AdminParentLinks /></AdminRoute>} />
        <Route path="/admin/prompts" element={<AdminRoute><AdminPrompts /></AdminRoute>} />
        <Route path="/admin/specifications" element={<AdminRoute><AdminSpecifications /></AdminRoute>} />
        <Route path="/admin/archive" element={<AdminRoute><AdminArchive /></AdminRoute>} />
        <Route path="/admin/webhook-events" element={<AdminRoute><AdminWebhookEvents /></AdminRoute>} />
        <Route path="/admin/question-bank" element={<AdminRoute><AdminQuestionBank /></AdminRoute>} />
         <Route path="/admin/resources" element={<AdminRoute><AdminResources /></AdminRoute>} />
         <Route path="/admin/resources/science-models" element={<AdminRoute><AdminScienceModels /></AdminRoute>} />
         <Route path="/admin/study-materials" element={<AdminRoute><AdminStudyMaterials /></AdminRoute>} />
         <Route path="/admin/student-uploads" element={<AdminRoute><AdminStudentUploads /></AdminRoute>} />
        <Route path="/admin/quote-bank" element={<AdminRoute><AdminQuoteBank /></AdminRoute>} />
          <Route path="/admin/revision-takeaways" element={<AdminRoute><LegacyRevisionTakeawaysRedirect /></AdminRoute>} />
        {process.env.NODE_ENV !== "production" && (
          <Route path="/admin/dev-tools" element={<AdminRoute><AdminDevTools /></AdminRoute>} />
        )}
        <Route path="/admin/system-settings" element={<AdminRoute><AdminSystemSettings /></AdminRoute>} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/help" element={<Help />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function RootLayout() {
  usePrerenderCleanup();
  const [clerkKey, setClerkKey] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState(false);
  const [configAttempt, setConfigAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let retryTimer;
    const load = async (attempts = 0) => {
      try {
        const r = await api.get("/config", { timeout: 15000 });
        if (!cancelled) {
          applyAppConfig(r.data);
          const key = r.data?.clerk_publishable_key;
          const env = r.data?.env; // "dev" or "prod"
          if (key) {
            const isProdKey = key.startsWith("pk_live_");
            const isDevKey = key.startsWith("pk_test_");
            if (env === "prod" && !isProdKey) {
              console.warn(
                "[Clerk] Key/environment mismatch: the server is running in production but the " +
                `publishable key starts with '${key.slice(0, 10)}…' instead of 'pk_live_'. ` +
                "Sign-in will be restricted to Clerk test users only."
              );
            } else if (env === "dev" && isProdKey) {
              console.warn(
                "[Clerk] Key/environment mismatch: the server is running in development but the " +
                "publishable key starts with 'pk_live_' (a production key). " +
                "Ensure CLERK_PUBLISHABLE_KEY_DEV is set correctly."
              );
            } else if (!isProdKey && !isDevKey && key) {
              console.warn(
                `[Clerk] Unrecognised publishable key prefix: '${key.slice(0, 10)}…'. ` +
                "Expected 'pk_live_' for production or 'pk_test_' for development."
              );
            }
            setClerkKey(key);
          }
          setConfigLoaded(true);
        }
      } catch (e) {
        if (!cancelled) {
          if (attempts >= 2) {
            setConfigError(true);
            return;
          }
          const delay = Math.min(2000 * (attempts + 1), 5000);
          retryTimer = setTimeout(() => load(attempts + 1), delay);
        }
      }
    };
    load();
    return () => { cancelled = true; clearTimeout(retryTimer); };
  }, [configAttempt]);

  if (!configLoaded) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-900 flex items-center justify-center">
            <span className="text-amber-400 font-bold text-sm">RF</span>
          </div>
          <span className="text-xl font-bold text-slate-800">Revision Foundry</span>
        </div>
        {configError ? (
          <div className="text-center space-y-3" role="alert">
            <p>We couldn’t load the app configuration. Please try again.</p>
            <button className="rounded-lg bg-blue-900 px-4 py-2 text-white" onClick={() => {
              setConfigError(false);
              setConfigAttempt((value) => value + 1);
            }}>Retry loading</button>
          </div>
        ) : <div className="w-6 h-6 border-2 border-blue-900 border-t-transparent rounded-full animate-spin" />}
      </div>
    );
  }

  if (!clerkKey) {
    return (
      <LegacyAuthProvider>
        <ViewAsBanner />
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </LegacyAuthProvider>
    );
  }

  return (
    <ClerkProvider publishableKey={clerkKey} afterSignOutUrl="/">
      <ClerkAuthProvider>
        <ViewAsBanner />
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </ClerkAuthProvider>
    </ClerkProvider>
  );
}

const router = createBrowserRouter([{ path: "*", element: <RootLayout /> }]);

function App() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    document.documentElement.classList.add("capacitor-native");
    let listener;
    let disposed = false;
    const handleOpen = ({ url } = {}) => {
      if (url) navigateToNativeAppUrl(url);
    };

    CapacitorApp.addListener("appUrlOpen", handleOpen).then((registered) => {
      if (disposed) registered.remove();
      else listener = registered;
    }).catch((error) => console.error("[NativeApp] Could not register deep-link listener:", error));

    CapacitorApp.getLaunchUrl().then(handleOpen).catch((error) => {
      console.error("[NativeApp] Could not read launch URL:", error);
    });

    return () => {
      disposed = true;
      document.documentElement.classList.remove("capacitor-native");
      listener?.remove();
    };
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <RouterProvider router={router} />
      </div>
    </HelmetProvider>
  );
}

export default App;
